import { useState, useEffect } from 'react'
import { FiAlertCircle, FiLock, FiMail, FiUser, FiX } from 'react-icons/fi'
import { startFirstAccess, completeEmailOnboarding } from '../services/api'

const BTN_M1 =
  'inline-flex min-h-[48px] w-full items-center justify-center border px-5 text-[14px] tracking-[0.04em] transition-colors disabled:cursor-not-allowed disabled:opacity-40'

const AUTH_STYLES = {
  legacy: {
    overlay:
      'fixed inset-0 z-[120] flex items-start justify-center bg-black/75 px-4 pt-16 pb-6 sm:pt-20',
    panel:
      'flex w-full max-w-md max-h-[calc(100dvh-4.5rem)] flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-xl',
    header: 'relative shrink-0 border-b border-gray-800/80 bg-gray-900 px-6 pb-3 pt-6 pr-14',
    close: 'absolute right-4 top-4 text-gray-400 hover:text-white',
    title: 'text-2xl font-bold text-white',
    subtitle: 'mt-1 text-sm text-gray-400',
    label: 'block text-sm text-gray-300 mb-2',
    iconInput: 'w-full pl-10 pr-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white',
    monoInput:
      'w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white font-mono text-sm',
    icon: 'absolute left-3 top-1/2 -translate-y-1/2 text-gray-500',
    error: 'text-sm text-red-400',
    primary: 'btn-primary w-full',
    primaryDisabled: 'btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed',
    secondary: 'btn-secondary w-full',
    link: 'text-sm text-cyan-400 hover:text-cyan-300 mt-4 block w-full text-center',
    question: 'block text-sm text-gray-300 mb-2',
    choiceOn: 'border-cyan-400 text-cyan-300 bg-cyan-500/10',
    choiceOff: 'border-gray-700 text-gray-300 bg-gray-800/80',
    choice: 'rounded-lg py-2 px-3 border text-sm',
    amberPlate: 'rounded-lg border border-amber-500/35 bg-amber-500/10 p-4 space-y-3',
    amberText: 'text-xs text-amber-100 leading-relaxed flex items-start gap-2 font-medium',
    amberIcon: 'mt-0.5 shrink-0 text-amber-400',
    strong: 'text-white',
    mutedText: 'text-xs text-gray-400 leading-relaxed pl-0 sm:pl-7',
    checkRow:
      'flex items-start gap-3 cursor-pointer group rounded-lg border border-white/10 bg-gray-900/60 p-3 hover:border-cyan-500/25 transition-colors',
    checkbox:
      'mt-0.5 w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500/50 focus:ring-offset-0 focus:ring-offset-gray-900',
    checkText: 'text-xs text-gray-300 leading-relaxed group-hover:text-gray-200',
    infoPlate: 'rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-3',
    infoText: 'text-xs text-cyan-100 leading-relaxed',
    infoStrong: 'font-semibold text-white',
    neutralPlate: 'rounded-lg border border-white/10 bg-gray-800/50 p-3',
    neutralText: 'text-xs text-gray-400 leading-relaxed',
    emailLine: 'text-sm text-gray-400',
    emailValue: 'text-white font-medium',
  },
  m1nt: {
    overlay:
      'fixed inset-0 z-[120] flex items-start justify-center bg-black/70 px-4 pt-16 pb-6 backdrop-blur-[2px] font-m1-ui text-m1-ink sm:pt-20',
    panel:
      'flex w-full max-w-md max-h-[calc(100dvh-4.5rem)] flex-col overflow-hidden border border-[#272C32] bg-[#18181A] shadow-[0_24px_48px_rgba(0,0,0,0.5)]',
    header: 'relative shrink-0 border-b border-[#272C32] px-6 pb-5 pt-6 pr-14',
    close:
      'absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center text-white transition-colors hover:text-m1-violet',
    title: 'font-m1-display text-[32px] font-normal leading-[1.1] tracking-[-0.015em] text-m1-ink',
    subtitle: 'mt-3 text-[15px] font-light leading-relaxed text-m1-ink-soft',
    label: 'mb-2 block text-[14px] text-m1-muted',
    iconInput:
      'min-h-[48px] w-full border border-m1-line bg-transparent py-3 pl-11 pr-4 text-[16px] font-light text-m1-ink placeholder:text-m1-muted focus:border-m1-violet focus:outline-none',
    monoInput:
      'min-h-[48px] w-full border border-m1-line bg-transparent px-4 py-3 font-m1-mono text-[14px] text-m1-ink placeholder:text-m1-muted focus:border-m1-violet focus:outline-none',
    icon: 'absolute left-3.5 top-1/2 -translate-y-1/2 text-m1-muted',
    error: 'text-[14px] text-m1-amber',
    primary: `${BTN_M1} border-m1-violet text-m1-violet hover:bg-m1-violet hover:text-m1-ground`,
    primaryDisabled: `${BTN_M1} border-m1-violet text-m1-violet hover:bg-m1-violet hover:text-m1-ground`,
    secondary: `${BTN_M1} border-white/30 text-m1-ink hover:border-m1-violet hover:text-m1-violet`,
    link: 'mt-5 block w-full min-h-[44px] text-center text-[14px] text-m1-violet transition-colors hover:text-m1-ink',
    question: 'mb-2 block text-[14px] text-m1-muted',
    choiceOn: 'border-m1-violet text-m1-violet',
    choiceOff: 'border-m1-line text-m1-ink-soft hover:border-white/30',
    choice: 'min-h-[44px] border px-3 text-[14px] tracking-[0.04em] transition-colors',
    amberPlate: 'space-y-3 rounded-2xl border border-m1-amber/35 bg-m1-amber/10 p-4',
    amberText: 'flex items-start gap-2 text-[13px] font-normal leading-relaxed text-m1-ink-soft',
    amberIcon: 'mt-0.5 shrink-0 text-m1-amber',
    strong: 'text-m1-ink',
    mutedText: 'pl-0 text-[13px] font-light leading-relaxed text-m1-muted sm:pl-7',
    checkRow:
      'group flex cursor-pointer items-start gap-3 border border-m1-line p-3 transition-colors hover:border-m1-violet',
    checkbox:
      'mt-0.5 h-4 w-4 rounded-none border-m1-line bg-transparent text-m1-violet focus:ring-m1-violet/50 focus:ring-offset-0',
    checkText: 'text-[13px] font-light leading-relaxed text-m1-ink-soft',
    infoPlate: 'rounded-2xl border border-m1-violet/35 bg-m1-violet/10 p-3',
    infoText: 'text-[13px] font-light leading-relaxed text-m1-ink-soft',
    infoStrong: 'font-normal text-m1-ink',
    neutralPlate: 'rounded-2xl border border-m1-line bg-m1-surface p-3',
    neutralText: 'text-[13px] font-light leading-relaxed text-m1-muted',
    emailLine: 'text-[14px] text-m1-muted',
    emailValue: 'font-normal text-m1-ink',
  },
}

export default function EmailAuthModal({
  isOpen,
  onClose,
  onLogin,
  onRegister,
  onFirstAccessComplete,
  /** 'm1nt' = Morpheus/M1NT website look; default keeps the legacy dark look. */
  variant = 'legacy',
}) {
  const ui = AUTH_STYLES[variant] || AUTH_STYLES.legacy
  const [mode, setMode] = useState('login')
  /** firstAccess: step 'email' (lookup) or 'setup' (profile + password) */
  const [firstAccessStep, setFirstAccessStep] = useState('email')
  const [faOnboardingToken, setFaOnboardingToken] = useState(null)
  const [faFromShopify, setFaFromShopify] = useState(false)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [registerPassword, setRegisterPassword] = useState('')
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('')
  const [hasCryptoWallet, setHasCryptoWallet] = useState(null)
  const [cryptoWallet, setCryptoWallet] = useState('')
  const [authorizedMintLabCustody, setAuthorizedMintLabCustody] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) return
    setMode('login')
    setFirstAccessStep('email')
    setFaOnboardingToken(null)
    setFaFromShopify(false)
    setName('')
    setEmail('')
    setLoginPassword('')
    setRegisterPassword('')
    setRegisterPasswordConfirm('')
    setHasCryptoWallet(null)
    setCryptoWallet('')
    setAuthorizedMintLabCustody(false)
    setError('')
    setSubmitting(false)
  }, [isOpen])

  const resetRegisterFields = () => {
    setName('')
    setRegisterPassword('')
    setRegisterPasswordConfirm('')
    setHasCryptoWallet(null)
    setCryptoWallet('')
    setAuthorizedMintLabCustody(false)
  }

  const validateRegisterLikeFields = () => {
    if (!name.trim()) return 'Please enter your full name'
    if (hasCryptoWallet === null) return 'Please tell us if you already have a crypto wallet'
    if (hasCryptoWallet) {
      const wallet = cryptoWallet.trim()
      if (!wallet) return 'Please enter your crypto wallet address'
      if (!/^0x[a-fA-F0-9]{40}$/.test(wallet)) return 'Please enter a valid wallet address (0x...)'
    }
    if (registerPassword.length < 8) return 'Password must be at least 8 characters'
    if (registerPassword !== registerPasswordConfirm) return 'Passwords do not match'
    if (hasCryptoWallet === false && !authorizedMintLabCustody) {
      return 'Please confirm that you understand and authorize M1NT Lab to mint on your behalf.'
    }
    return null
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) return setError('Please enter your email')
    if (!loginPassword) return setError('Please enter your password')
    setSubmitting(true)
    try {
      await onLogin({ email: normalizedEmail, password: loginPassword })
      setLoginPassword('')
    } catch (err) {
      setError(err.message || 'Authentication failed')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) return setError('Please enter your email')
    const regErr = validateRegisterLikeFields()
    if (regErr) return setError(regErr)
    setSubmitting(true)
    try {
      await onRegister({
        name: name.trim(),
        email: normalizedEmail,
        password: registerPassword,
        hasCryptoWallet,
        cryptoWallet: hasCryptoWallet ? cryptoWallet.trim() : null,
      })
      setEmail('')
      resetRegisterFields()
    } catch (err) {
      setError(err.message || 'Authentication failed')
    } finally {
      setSubmitting(false)
    }
  }

  const handleFirstAccessEmailContinue = async (e) => {
    e.preventDefault()
    setError('')
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) return setError('Please enter your email')
    setSubmitting(true)
    try {
      const res = await startFirstAccess({ email: normalizedEmail })
      const d = res.data
      setFaOnboardingToken(d.onboardingToken)
      setFaFromShopify(d.fromShopify === true)
      setName(String(d.user?.name || '').trim())
      setFirstAccessStep('setup')
      setHasCryptoWallet(null)
      setCryptoWallet('')
      setAuthorizedMintLabCustody(false)
      setRegisterPassword('')
      setRegisterPasswordConfirm('')
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Could not continue first access'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFirstAccessSetupSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!faOnboardingToken) return setError('Session expired. Start again from First access.')
    const regErr = validateRegisterLikeFields()
    if (regErr) return setError(regErr)
    setSubmitting(true)
    try {
      const res = await completeEmailOnboarding(faOnboardingToken, {
        name: name.trim(),
        password: registerPassword,
        hasCryptoWallet,
        cryptoWallet: hasCryptoWallet ? cryptoWallet.trim() : undefined,
      })
      const d = res.data
      if (typeof onFirstAccessComplete === 'function') {
        onFirstAccessComplete({
          user: d.user,
          emailAuthToken: d.emailAuthToken,
        })
      }
      resetRegisterFields()
      setFaOnboardingToken(null)
      setMode('login')
      setFirstAccessStep('email')
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Could not complete setup'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const goToLogin = () => {
    setError('')
    setMode('login')
    setFirstAccessStep('email')
    setFaOnboardingToken(null)
    setFaFromShopify(false)
    resetRegisterFields()
  }

  const startFirstAccessFlow = () => {
    setError('')
    setMode('firstAccess')
    setFirstAccessStep('email')
    setFaOnboardingToken(null)
    setFaFromShopify(false)
    resetRegisterFields()
    setLoginPassword('')
  }

  if (!isOpen) return null

  const registerWalletBlock = (
    <>
      <div>
        <p className={ui.question}>Do you already have a crypto wallet?</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`${ui.choice} ${hasCryptoWallet === true ? ui.choiceOn : ui.choiceOff}`}
            onClick={() => {
              setHasCryptoWallet(true)
              setAuthorizedMintLabCustody(false)
            }}
            disabled={submitting}
          >
            Yes
          </button>
          <button
            type="button"
            className={`${ui.choice} ${hasCryptoWallet === false ? ui.choiceOn : ui.choiceOff}`}
            onClick={() => {
              setHasCryptoWallet(false)
              setAuthorizedMintLabCustody(false)
            }}
            disabled={submitting}
          >
            No
          </button>
        </div>
      </div>

      {hasCryptoWallet === true && (
        <div>
          <label className={ui.label}>Crypto Wallet Address</label>
          <input
            type="text"
            className={ui.monoInput}
            placeholder="0x..."
            value={cryptoWallet}
            onChange={(e) => setCryptoWallet(e.target.value)}
            disabled={submitting}
          />
        </div>
      )}

      {hasCryptoWallet === false && (
        <div className={ui.amberPlate}>
          <p className={ui.amberText}>
            <FiAlertCircle className={ui.amberIcon} />
            <span>
              Your cards will be minted using{' '}
              <span className={ui.strong}>M1NT Lab&apos;s custody crypto wallet</span>. NFTs stay
              under M1NT Lab custody until you add your own wallet to your account.
            </span>
          </p>
          <p className={ui.mutedText}>
            Once you have a personal crypto wallet, you can request transfer of your NFTs to your
            wallet through M1NT Lab support and the owner portal flows.
          </p>
          <label className={ui.checkRow}>
            <input
              type="checkbox"
              checked={authorizedMintLabCustody}
              onChange={(e) => setAuthorizedMintLabCustody(e.target.checked)}
              disabled={submitting}
              className={ui.checkbox}
            />
            <span className={ui.checkText}>
              I understand and I authorize M1NT Lab to mint and hold my NFTs under this custody
              arrangement until I provide my own wallet.
            </span>
          </label>
        </div>
      )}
    </>
  )

  const registerPasswordBlock = (
    <>
      <div>
        <label className={ui.label}>Password</label>
        <div className="relative">
          <FiLock className={ui.icon} />
          <input
            type="password"
            className={ui.iconInput}
            placeholder="At least 8 characters"
            value={registerPassword}
            onChange={(e) => setRegisterPassword(e.target.value)}
            disabled={submitting}
            autoComplete="new-password"
            minLength={8}
          />
        </div>
      </div>
      <div>
        <label className={ui.label}>Confirm password</label>
        <div className="relative">
          <FiLock className={ui.icon} />
          <input
            type="password"
            className={ui.iconInput}
            placeholder="Repeat password"
            value={registerPasswordConfirm}
            onChange={(e) => setRegisterPasswordConfirm(e.target.value)}
            disabled={submitting}
            autoComplete="new-password"
            minLength={8}
          />
        </div>
      </div>
    </>
  )

  let title = 'Login'
  let subtitle = 'Sign in with your email and password.'
  if (mode === 'register') {
    title = 'Create account'
    subtitle = 'Register with email and create a password.'
  } else if (mode === 'firstAccess') {
    title = 'First access'
    subtitle =
      firstAccessStep === 'email'
        ? 'Enter the email you used on Shopify or the one M1NT Lab sent you. We will verify your account.'
        : 'Complete your profile and create a password to open the owner portal.'
  }

  return (
    <div className={ui.overlay}>
      <div className={ui.panel}>
        <div className={ui.header}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={ui.close}
            aria-label="Close"
          >
            <FiX size={20} />
          </button>

          <h2 className={ui.title}>{title}</h2>
          <p className={ui.subtitle}>{subtitle}</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
          {mode === 'login' && (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label className={ui.label}>Email</label>
                <div className="relative">
                  <FiMail className={ui.icon} />
                  <input
                    type="email"
                    className={ui.iconInput}
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              <div>
                <label className={ui.label}>Password</label>
                <div className="relative">
                  <FiLock className={ui.icon} />
                  <input
                    type="password"
                    className={ui.iconInput}
                    placeholder="Your password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    disabled={submitting}
                    autoComplete="current-password"
                  />
                </div>
              </div>
              {error ? <p className={ui.error}>{error}</p> : null}
              <button type="submit" disabled={submitting} className={ui.primary}>
                {submitting ? 'Please wait...' : 'Login'}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={startFirstAccessFlow}
                className={ui.secondary}
              >
                First access
              </button>
            </form>
          )}

          {mode === 'register' && (
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              <div>
                <label className={ui.label}>Full Name</label>
                <div className="relative">
                  <FiUser className={ui.icon} />
                  <input
                    type="text"
                    className={ui.iconInput}
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              {registerWalletBlock}
              <div>
                <label className={ui.label}>Email</label>
                <div className="relative">
                  <FiMail className={ui.icon} />
                  <input
                    type="email"
                    className={ui.iconInput}
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              {registerPasswordBlock}
              {error ? <p className={ui.error}>{error}</p> : null}
              <button
                type="submit"
                disabled={submitting || (hasCryptoWallet === false && !authorizedMintLabCustody)}
                className={ui.primaryDisabled}
              >
                {submitting ? 'Please wait...' : 'Register'}
              </button>
            </form>
          )}

          {mode === 'firstAccess' && firstAccessStep === 'email' && (
            <form onSubmit={handleFirstAccessEmailContinue} className="space-y-4">
              <div>
                <label className={ui.label}>Email</label>
                <div className="relative">
                  <FiMail className={ui.icon} />
                  <input
                    type="email"
                    className={ui.iconInput}
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              {error ? <p className={ui.error}>{error}</p> : null}
              <button type="submit" disabled={submitting} className={ui.primary}>
                {submitting ? 'Please wait...' : 'Continue'}
              </button>
              <button
                type="button"
                className={ui.secondary}
                disabled={submitting}
                onClick={goToLogin}
              >
                Back to login
              </button>
            </form>
          )}

          {mode === 'firstAccess' && firstAccessStep === 'setup' && (
            <form onSubmit={handleFirstAccessSetupSubmit} className="space-y-4">
              {faFromShopify && (
                <div className={ui.infoPlate}>
                  <p className={ui.infoText}>
                    We found a M1NT owner account linked to your{' '}
                    <span className={ui.infoStrong}>Shopify</span> purchases or M1NT credits for
                    this email. Finish setup below.
                  </p>
                </div>
              )}
              {!faFromShopify && (
                <div className={ui.neutralPlate}>
                  <p className={ui.neutralText}>
                    Account verified. If you expected a Shopify link and don&apos;t see it, make
                    sure you use the same email as your order; contact M1NT Lab if you need help.
                  </p>
                </div>
              )}
              <div className={ui.emailLine}>
                Email: <span className={ui.emailValue}>{email.trim() || '—'}</span>
              </div>
              <div>
                <label className={ui.label}>Full name</label>
                <div className="relative">
                  <FiUser className={ui.icon} />
                  <input
                    type="text"
                    className={ui.iconInput}
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              {registerWalletBlock}
              {registerPasswordBlock}
              {error ? <p className={ui.error}>{error}</p> : null}
              <button
                type="submit"
                disabled={submitting || (hasCryptoWallet === false && !authorizedMintLabCustody)}
                className={ui.primaryDisabled}
              >
                {submitting ? 'Please wait...' : 'Complete setup'}
              </button>
              <button
                type="button"
                className={ui.secondary}
                disabled={submitting}
                onClick={() => {
                  setFirstAccessStep('email')
                  setFaOnboardingToken(null)
                  setError('')
                }}
              >
                Back
              </button>
            </form>
          )}

          {mode === 'login' && (
            <button
              type="button"
              className={ui.link}
              onClick={() => {
                setError('')
                setMode('register')
                setLoginPassword('')
                resetRegisterFields()
              }}
            >
              Don&apos;t have an account? Register
            </button>
          )}

          {mode === 'register' && (
            <button
              type="button"
              className={ui.link}
              onClick={() => {
                setError('')
                setMode('login')
                resetRegisterFields()
                setEmail('')
              }}
            >
              Already have an account? Login
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
